import { createRoot, hydrateRoot } from 'react-dom/client';
import { loadContentParityTranslations } from './content/loadContentParityTranslations';
import { loadMessages } from './i18n/catalogLoader';
import type { Locale } from './i18n/messages';
import { matchPathname } from './routing/siteRoutes';
import {
  APPLE_ITUNES_APP_META_NAME,
  getAppleSmartAppBannerContent,
  shouldExposeAppleSmartAppBanner,
} from './smartAppBanner';
import './styles/tokens.css';
import './styles/typography.css';
import './index.css';
import './components/SiteHeader.css';

const rootElement = document.getElementById('root')!;

const LOCALE_PREFIXES: Record<string, Locale> = {
  es: 'es',
  fr: 'fr',
  ar: 'ar',
  zh: 'zh-Hans',
  de: 'de',
  pt: 'pt',
};

function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/index.html') return '/';
  if (pathname.length > 1 && pathname.endsWith('/')) return pathname.slice(0, -1);
  return pathname;
}

function decodePathSegments(pathname: string): string[] {
  return normalizePathname(pathname)
    .split('/')
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment));
}

function getInitialLocale(pathname: string): Locale {
  const segments = decodePathSegments(pathname);
  const locale = segments[0] ? LOCALE_PREFIXES[segments[0]] : undefined;
  return locale ?? 'en';
}

function shouldRegisterServiceWorker() {
  if (typeof window === 'undefined') return false;
  if (!import.meta.env.PROD) return false;
  const host = window.location.hostname.toLowerCase();
  return host === 'passlens.com' || host === 'www.passlens.com';
}

function syncAppleSmartAppBannerMeta(enabled: boolean, pathname?: string) {
  const selector = `meta[name="${APPLE_ITUNES_APP_META_NAME}"]`;
  const current = document.head.querySelector<HTMLMetaElement>(selector);

  if (!enabled) {
    current?.remove();
    return;
  }

  const meta = current ?? document.createElement('meta');
  meta.name = APPLE_ITUNES_APP_META_NAME;
  meta.content = getAppleSmartAppBannerContent(pathname);

  if (!current) {
    document.head.appendChild(meta);
  }
}

async function cleanupNonProductionServiceWorkers() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch {
    // Best-effort cleanup only.
  }

  if (!('caches' in window)) return;
  try {
    const keys = await caches.keys();
    await Promise.all(
      keys
        .filter((key) => key.startsWith('passlens-'))
        .map((key) => caches.delete(key)),
    );
  } catch {
    // Best-effort cleanup only.
  }
}

async function bootstrap() {
  const pathname = window.location.pathname;
  const initialLocale = getInitialLocale(pathname);
  const route = matchPathname(pathname);
  const appShell = route.page === 'app';
  const contentParityPromise =
    initialLocale === 'en' || appShell
      ? Promise.resolve()
      : loadContentParityTranslations(initialLocale);
  const appleSmartAppBanner = shouldExposeAppleSmartAppBanner(route);
  const bootstrapModulePromise = appShell
    ? import('./bootstrapApp').then((module) => ({
        create: module.createBootstrapApp,
      }))
    : route.page === 'landing'
      ? import('./bootstrapSite').then((module) => ({
          create: module.createBootstrapSite,
        }))
      : import('./bootstrapContent').then((module) => ({
          create: module.createBootstrapContent,
        }));

  syncAppleSmartAppBannerMeta(appleSmartAppBanner, pathname);
  document.documentElement.classList.toggle('passlens-app-shell', appShell);
  document.body.classList.toggle('passlens-app-shell', appShell);
  rootElement.classList.toggle('passlens-app-shell', appShell);
  document.documentElement.style.overflow = appShell ? 'hidden' : '';
  document.documentElement.style.overflowY = appShell ? 'hidden' : '';
  document.body.style.overflow = appShell ? 'hidden' : '';
  document.body.style.overflowY = appShell ? 'hidden' : '';
  rootElement.style.overflow = appShell ? 'hidden' : '';
  rootElement.style.overflowY = appShell ? 'hidden' : '';

  const [initialMessages, bootstrapModule] = await Promise.all([
    loadMessages(initialLocale),
    bootstrapModulePromise,
    contentParityPromise,
  ]);
  const app = bootstrapModule.create(initialLocale, initialMessages);

  if (rootElement.dataset.prerendered === '1') {
    hydrateRoot(rootElement, app);
  } else {
    createRoot(rootElement).render(app);
  }
}

void bootstrap();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    if (shouldRegisterServiceWorker()) {
      navigator.serviceWorker.register('/sw.js').catch(() => {
        // Keep this silent; the app remains usable without PWA install support.
      });
      return;
    }

    void cleanupNonProductionServiceWorkers();
  });
}
